import * as React from 'react';
import { Controller } from 'react-hook-form';
import { Label } from '../../../ui/label';
import { ActionNode } from '../../types';
import { DocumentRendererContext } from '../../renderer/types';
import { AttestationAlert } from '../views/AttestationAlert';
import { DocumentCheckbox } from './DocumentCheckbox';
import { DocumentSelect } from './DocumentSelect';

interface DocumentAttestationProps {
    field: ActionNode;
    context: DocumentRendererContext;
}

/**
 * Composite attestation node (action checkbox with an `attestation` config).
 *
 * - mode 'self': the current editor signs as themselves.
 * - mode 'requested': a signer is selected from department users and only
 *   that user can sign (the existing signature-request machinery — badge,
 *   filter, notification — keys off the node's notification/signerFieldId).
 *
 * On signing the component records optimistic values under derived data
 * keys (<id>By, <id>At, <id>Timezone); the server overwrites these with an
 * authoritative timestamp and adds <id>Signature = {signedAt, timezone, ip,
 * userName, hash} when the save carrying the transition is persisted.
 */
export function DocumentAttestation({
    field,
    context,
}: DocumentAttestationProps) {
    const { control, watch, setValue, updateData, checkDisable, contextData } =
        context;

    const mode = field.attestation?.mode || 'self';
    const attestationId = field.id;
    const signerFieldId = field.signerFieldId || `${attestationId}SignerId`;

    const getValue = (key: string): any => {
        const watchedValue = watch(key);
        if (watchedValue !== undefined) {
            return watchedValue;
        }
        return contextData?.[key];
    };

    const attested = getValue(attestationId) === true;
    const attestedBy = getValue(`${attestationId}By`);
    const attestedAt = getValue(`${attestationId}At`);
    const signerId = mode === 'requested' ? getValue(signerFieldId) : undefined;

    const currentUserId = contextData?.currentUserId || '';
    const attestationName = contextData?.attestationName || '';
    const departmentUsers: Array<{ label?: string; value?: string }> =
        (Array.isArray(context.dataSources?.departmentUsers)
            ? context.dataSources.departmentUsers
            : undefined) ||
        (Array.isArray(contextData?.departmentUsers)
            ? contextData.departmentUsers
            : []);

    const baseDisabled = checkDisable(field.disableWhen);
    const signerLocked =
        mode === 'requested' &&
        (!signerId || (currentUserId && signerId !== currentUserId));
    const checkboxDisabled = baseDisabled || attested || !!signerLocked;

    const pendingSignerName =
        !attested && signerId
            ? departmentUsers.find((user) => user?.value === signerId)?.label ||
              'a colleague'
            : undefined;

    const writeValue = (key: string, value: any) => {
        setValue(key, value);
        updateData(key, value);
    };

    const handleSign = (onChange: (value: any) => void) => {
        const signedAt = new Date().toISOString();
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        onChange(true);
        updateData(attestationId, true);
        writeValue(`${attestationId}By`, attestationName);
        writeValue(`${attestationId}At`, signedAt);
        writeValue(`${attestationId}Timezone`, timezone);
    };

    return (
        <div className="space-y-3">
            {mode === 'requested' && (
                <div className="space-y-1.5 sm:max-w-sm">
                    <Label className="text-sm font-medium">
                        Signature requested from
                    </Label>
                    <Controller
                        name={signerFieldId}
                        control={control}
                        defaultValue={contextData?.[signerFieldId] ?? ''}
                        render={({ field: { onChange, value } }) => (
                            <DocumentSelect
                                // A requested signature is for someone
                                // else — exclude the current user.
                                options={departmentUsers.filter(
                                    (
                                        user
                                    ): user is {
                                        label: string;
                                        value: string;
                                    } =>
                                        !!user?.value &&
                                        user.value !== currentUserId
                                )}
                                value={value || ''}
                                onChange={(nextValue) => {
                                    onChange(nextValue);
                                    updateData(signerFieldId, nextValue);
                                }}
                                placeholder="Select the user who must sign"
                                disabled={baseDisabled || attested}
                            />
                        )}
                    />
                </div>
            )}

            <AttestationAlert
                attested={attested}
                attestationName={attestedBy}
                signOffDate={attestedAt}
                pendingSignerName={pendingSignerName}
            />

            <Controller
                name={attestationId}
                control={control}
                defaultValue={contextData?.[attestationId] ?? false}
                render={({ field: { onChange, value } }) => (
                    <DocumentCheckbox
                        checked={value === true}
                        onCheckedChange={(checked) => {
                            if (!checked) return;
                            handleSign(onChange);
                        }}
                        label={field.title}
                        confirmation={field.confirmation}
                        disabled={checkboxDisabled}
                    />
                )}
            />
        </div>
    );
}
